import { LitElement, html, nothing, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { MECHANIC_INFO } from '@juegazo/content';
import type { Mechanic } from '@juegazo/shared';

const ALL_MECHANIC_KEYS: Mechanic[] = [
  'category',
  'starts_with_letter',
  'contains',
  'excludes_letter',
  'chain',
  'dual_condition',
  'name_three',
];

const TOTAL_STEPS = 5;

interface TutorialStep {
  title: string;
  body: string;
  subtitle?: string;
  illustration: 'bomb' | 'pass' | 'podium' | 'devices' | null;
}

const STEPS: TutorialStep[] = [
  {
    title: '¡Bienvenido a Juegazo!',
    body: 'Un juego de respuestas rápidas donde la bomba va pasando de jugador en jugador. Juega online con amigos o todos en el mismo dispositivo.',
    illustration: 'bomb',
  },
  {
    title: 'Tienes la bomba',
    body: 'Cuando empieza tu turno, te dan un reto (una categoría, una letra, etc.). Tienes que decir respuestas válidas en voz alta y PASAR la bomba antes de que se acabe el tiempo. Si la bomba explota en tus manos, sumas una explosión.',
    illustration: 'pass',
  },
  {
    title: 'Existen 7 mecánicas para jugar',
    body: '',
    illustration: null,
  },
  {
    title: 'Menos explosiones, menos tiempo',
    body: 'Al final de la partida, gana quien tenga MENOS explosiones. Si hay empate en explosiones, gana quien haya acumulado MENOS tiempo en sus respuestas.',
    subtitle: 'Empate → desempate con una bomba final solo entre los empatados.',
    illustration: 'podium',
  },
  {
    title: 'Modos de juego y reconexión',
    body: '',
    illustration: 'devices',
  },
];

@customElement('jgz-tutorial')
export class JgzTutorial extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Boolean }) open = false;
  @state() private currentStep = 0;

  private overlayHost: HTMLDivElement | null = null;
  private boundKeydown: ((e: KeyboardEvent) => void) | null = null;

  private getOverlayHost(): HTMLDivElement {
    if (!this.overlayHost) {
      this.overlayHost = document.createElement('div');
      document.body.appendChild(this.overlayHost);
    }
    return this.overlayHost;
  }

  private syncOverlay() {
    const host = this.getOverlayHost();
    render(this.open ? this.renderOverlay() : nothing, host);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.detachKeydown();
    this.overlayHost?.remove();
    this.overlayHost = null;
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('open')) {
      if (this.open) {
        this.attachKeydown();
      } else {
        this.detachKeydown();
        // Reset for the next time the tutorial opens.
        this.currentStep = 0;
      }
    }
    this.syncOverlay();
  }

  private attachKeydown() {
    if (this.boundKeydown) return;
    this.boundKeydown = (e: KeyboardEvent) => this.onKeydown(e);
    document.addEventListener('keydown', this.boundKeydown);
  }

  private detachKeydown() {
    if (!this.boundKeydown) return;
    document.removeEventListener('keydown', this.boundKeydown);
    this.boundKeydown = null;
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (!this.open) return;
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key === 'ArrowRight') {
      this.next();
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft') {
      this.prev();
      e.preventDefault();
    }
  };

  private next = () => {
    if (this.currentStep === TOTAL_STEPS - 1) {
      this.close();
      return;
    }
    this.currentStep += 1;
  };

  private prev = () => {
    if (this.currentStep > 0) this.currentStep -= 1;
  };

  private goToStep = (i: number) => {
    if (i >= 0 && i < TOTAL_STEPS) this.currentStep = i;
  };

  private close = () => {
    this.dispatchEvent(
      new CustomEvent('jgz-close', { bubbles: true, composed: true }),
    );
  };

  private renderOverlay() {
    return html`
      <div
        class="tutorial-backdrop"
        @click=${this.close}
      ></div>
      <div
        class="tutorial"
        role="dialog"
        aria-label="Cómo jugar"
        aria-modal="true"
      >
        <div class="tutorial-header">
          <div class="tutorial-dots" role="tablist" aria-label="Pasos del tutorial">
            ${Array.from({ length: TOTAL_STEPS }, (_, i) => {
              const active = i === this.currentStep;
              return html`
                <button
                  type="button"
                  class="tutorial-dot ${active ? 'is-active' : ''}"
                  role="tab"
                  aria-selected=${active ? 'true' : 'false'}
                  aria-label=${`Paso ${i + 1} de ${TOTAL_STEPS}`}
                  @click=${() => this.goToStep(i)}
                ></button>
              `;
            })}
          </div>
          <button
            class="tutorial-close"
            type="button"
            @click=${this.close}
            aria-label="Cerrar tutorial"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="tutorial-content">
          ${keyed(this.currentStep, html`
            <div class="tutorial-step" data-step=${this.currentStep}>
              ${this.renderIllustration(STEPS[this.currentStep].illustration)}
              <h2 class="tutorial-title">${STEPS[this.currentStep].title}</h2>
              <div class="tutorial-body">
                ${this.renderBody(this.currentStep)}
              </div>
            </div>
          `)}
        </div>
        <div class="tutorial-footer">
          <button
            class="btn-pill gray"
            type="button"
            ?disabled=${this.currentStep === 0}
            @click=${this.prev}
          >
            <span class="label">ANTERIOR</span>
          </button>
          <button
            class="btn-pill primary"
            type="button"
            @click=${this.next}
          >
            <span class="label">${this.currentStep === TOTAL_STEPS - 1
              ? 'CERRAR TUTORIAL'
              : 'SIGUIENTE'}</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderBody(stepIndex: number) {
    if (stepIndex === 2) {
      return html`
        <div class="tutorial-mechanics-grid" role="list">
          ${ALL_MECHANIC_KEYS.map((key) => {
            const info = MECHANIC_INFO[key];
            return html`
              <div class="tutorial-mechanic-card" role="listitem">
                <span class="tutorial-mechanic-card-name">${info.name}</span>
                <span class="tutorial-mechanic-card-desc">${info.description}</span>
                <span class="tutorial-mechanic-card-example">${info.example}</span>
              </div>
            `;
          })}
        </div>
      `;
    }
    if (stepIndex === 4) {
      return html`
        <div class="tutorial-bullets">
          <p>
            <strong>Online:</strong> creas una sala y compartes el código con tus
            amigos. Hay chat de voz.
          </p>
          <p>
            <strong>Local:</strong> todos en el mismo dispositivo, pasándose el
            dispositivo cuando se acaba el turno.
          </p>
          <p>
            <strong>Reconexión:</strong> en el lobby y entre una bomba y otra,
            puedes volver a entrar si por error pierdes conexión. Una vez que
            arrancó la partida, si son 2 jugadores y uno se desconecta, la
            partida termina; si son 3 o más, el que se va queda fuera y los
            demás siguen.
          </p>
        </div>
      `;
    }
    const step = STEPS[stepIndex];
    return html`
      <p>${step.body}</p>
      ${step.subtitle ? html`<p class="tutorial-subtitle">${step.subtitle}</p>` : nothing}
    `;
  }

  private renderIllustration(kind: TutorialStep['illustration']) {
    if (kind === null) return nothing;
    if (kind === 'bomb') return this.renderBombIllustration();
    if (kind === 'pass') return this.renderPassIllustration();
    if (kind === 'podium') return this.renderPodiumIllustration();
    return this.renderDevicesIllustration();
  }

  private renderBombIllustration() {
    return html`
      <div class="tutorial-illustration" aria-hidden="true">
        <svg viewBox="0 0 120 120" width="120" height="120">
          <path
            d="M75 38 Q 88 24, 92 12"
            stroke="#ffb703"
            stroke-width="3"
            fill="none"
            stroke-linecap="round"
          />
          <circle cx="92" cy="12" r="8" fill="#ffb703" opacity="0.35">
            <animate
              attributeName="r"
              values="6;14;6"
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.5;0;0.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx="92" cy="12" r="4" fill="#ffb703">
            <animate
              attributeName="r"
              values="4;2.5;4"
              dur="0.5s"
              repeatCount="indefinite"
            />
          </circle>
          <circle
            cx="60"
            cy="68"
            r="36"
            fill="#023047"
            stroke="#ffb703"
            stroke-width="3"
          />
          <ellipse cx="48" cy="55" rx="9" ry="6" fill="#8ecae6" opacity="0.5" />
          <text
            x="60"
            y="76"
            text-anchor="middle"
            font-family="ui-monospace, SFMono-Regular, Menlo, monospace"
            font-size="16"
            font-weight="800"
            fill="#ffb703"
          >
            00:10
          </text>
        </svg>
      </div>
    `;
  }

  private renderPassIllustration() {
    return html`
      <div class="tutorial-illustration" aria-hidden="true">
        <svg viewBox="0 0 220 130" width="220" height="130">
          <defs>
            <marker
              id="tut-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L 10 5 L 0 10 z" fill="#ffb703" />
            </marker>
          </defs>

          <!-- Figure 1 (left) -->
          <circle cx="35" cy="35" r="13" fill="#8ecae6" />
          <rect x="22" y="50" width="26" height="40" rx="6" fill="#8ecae6" />
          <text
            x="35"
            y="115"
            text-anchor="middle"
            fill="#ffb703"
            font-size="11"
            font-weight="800"
          >
            TÚ
          </text>

          <!-- Arrow from figure 1 toward bomb -->
          <path
            d="M 60 60 Q 80 55, 95 55"
            stroke="#ffb703"
            stroke-width="2"
            stroke-dasharray="3,2"
            fill="none"
            stroke-linecap="round"
            marker-end="url(#tut-arrow)"
          />

          <!-- Bomb in the middle -->
          <circle
            cx="110"
            cy="55"
            r="11"
            fill="#023047"
            stroke="#ffb703"
            stroke-width="2"
          />
          <path
            d="M117 48 Q 125 42, 127 36"
            stroke="#ffb703"
            stroke-width="1.5"
            fill="none"
            stroke-linecap="round"
          />
          <circle cx="127" cy="36" r="2.5" fill="#ffb703">
            <animate
              attributeName="opacity"
              values="1;0.3;1"
              dur="0.5s"
              repeatCount="indefinite"
            />
           </circle>

          <!-- Arrow from bomb to figure 2 -->
          <path
            d="M 125 55 Q 145 55, 165 55"
            stroke="#ffb703"
            stroke-width="2"
            stroke-dasharray="3,2"
            fill="none"
            stroke-linecap="round"
            marker-end="url(#tut-arrow)"
          />

          <!-- Figure 2 (right) -->
          <circle cx="185" cy="35" r="13" fill="#219ebc" />
          <rect x="172" y="50" width="26" height="40" rx="6" fill="#219ebc" />
          <text
            x="185"
            y="115"
            text-anchor="middle"
            fill="#8ecae6"
            font-size="9"
            font-weight="800"
          >
            OTRO
          </text>
        </svg>
      </div>
    `;
  }

  private renderPodiumIllustration() {
    return html`
      <div class="tutorial-illustration" aria-hidden="true">
        <svg viewBox="0 0 200 130" width="200" height="130">
          <!-- 2nd place -->
          <rect x="20" y="55" width="55" height="65" fill="#8ecae6" rx="6" />
          <text
            x="47.5"
            y="97"
            text-anchor="middle"
            fill="#023047"
            font-weight="900"
            font-size="24"
          >
            2
          </text>
          <!-- 1st place -->
          <rect x="78" y="20" width="55" height="100" fill="#ffb703" rx="6" />
          <path d="M 95 30 L 105 30 L 100 18 Z" fill="#fb8500" />
          <text
            x="105.5"
            y="72"
            text-anchor="middle"
            fill="#023047"
            font-weight="900"
            font-size="28"
          >
            1
          </text>
          <!-- 3rd place -->
          <rect x="136" y="75" width="50" height="45" fill="#fb8500" rx="6" />
          <text
            x="161"
            y="105"
            text-anchor="middle"
            fill="#023047"
            font-weight="900"
            font-size="18"
          >
            3
          </text>
        </svg>
      </div>
    `;
  }

  private renderDevicesIllustration() {
    return html`
      <div class="tutorial-illustration" aria-hidden="true">
        <svg viewBox="0 0 220 110" width="220" height="110">
          <!-- Device 1 (ON) -->
          <rect
            x="20"
            y="20"
            width="70"
            height="50"
            rx="6"
            fill="#023047"
            stroke="#10b981"
            stroke-width="2.5"
          />
          <circle cx="28" cy="28" r="2.5" fill="#10b981">
            <animate
              attributeName="opacity"
              values="1;0.3;1"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
          <text
            x="55"
            y="52"
            text-anchor="middle"
            fill="#10b981"
            font-size="14"
            font-weight="900"
          >
            ON
          </text>
          <text
            x="55"
            y="88"
            text-anchor="middle"
            fill="#fff"
            font-size="11"
            font-weight="700"
          >
            Vos
          </text>

          <!-- Device 2 (OFF) -->
          <rect
            x="130"
            y="20"
            width="70"
            height="50"
            rx="6"
            fill="#023047"
            stroke="#fb8500"
            stroke-width="2.5"
          />
          <circle cx="138" cy="28" r="2.5" fill="#fb8500" />
          <text
            x="165"
            y="52"
            text-anchor="middle"
            fill="#fb8500"
            font-size="12"
            font-weight="900"
          >
            OFF
          </text>
          <text
            x="165"
            y="88"
            text-anchor="middle"
            fill="#fff"
            font-size="11"
            font-weight="700"
          >
            Amigo
          </text>

          <!-- Connection line -->
          <line
            x1="90"
            y1="45"
            x2="130"
            y2="45"
            stroke="#8ecae6"
            stroke-width="2.5"
            stroke-dasharray="5,3"
            stroke-linecap="round"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-16"
              dur="0.8s"
              repeatCount="indefinite"
            />
          </line>
          <circle cx="110" cy="45" r="3" fill="#ffb703">
            <animate
              attributeName="opacity"
              values="0.4;1;0.4"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-tutorial': JgzTutorial;
  }
}
